"use client";

import {
  type DragEvent,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useState,
} from "react";

import { RightIcon, UploadBlack } from "@/components/ui/primitives/icons";
import { cn } from "@/lib/utils";

/* ── Types ── */

export type ParsingStatus =
  | "COMPLETED"
  | "PROCESSING"
  | "PENDING"
  | "FAILED"
  | "";

export interface ParsingAccordionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onDrop"> {
  /** Section title */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Accepted file extensions (e.g. [".dwg", ".ifc", ".rvt"]) */
  acceptExtensions: string[];
  /** Current parsing status */
  status?: ParsingStatus;
  /** Whether the section is open by default */
  defaultOpen?: boolean;
  /** Called when a file is dropped on the accordion */
  onFileDrop?: (file: File) => void;
  /** Called when a file with wrong extension is dropped */
  onFileDropError?: (file: File) => void;
  /** Whether the section is locked (e.g. completed structural parsing) */
  locked?: boolean;
  /** Accordion body content */
  children: ReactNode;
}

/* ── Status helpers ── */

const STATUS_MAP: Record<
  string,
  { label: string; dotClass: string; badgeClass: string }
> = {
  COMPLETED: {
    label: "완료",
    dotClass: "bg-[var(--color-text-information)]",
    badgeClass:
      "bg-[var(--color-element-information-light)] text-[var(--color-text-information)]",
  },
  PROCESSING: {
    label: "처리 중",
    dotClass: "bg-[var(--color-text-success)] animate-pulse",
    badgeClass:
      "bg-[var(--color-element-sucess-light)] text-[var(--color-text-success)]",
  },
  PENDING: {
    label: "대기",
    dotClass: "bg-[var(--color-text-warning)]",
    badgeClass:
      "bg-[var(--color-element-warning-light)] text-[var(--color-text-warning)]",
  },
  FAILED: {
    label: "실패",
    dotClass: "bg-[var(--color-text-danger)]",
    badgeClass:
      "bg-[var(--color-element-danger-light)] text-[var(--color-text-danger)]",
  },
};

/* ── Component ── */

const ParsingAccordion = forwardRef<HTMLDivElement, ParsingAccordionProps>(
  (
    {
      title,
      description,
      acceptExtensions,
      status,
      defaultOpen = false,
      onFileDrop,
      onFileDropError,
      locked = false,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(defaultOpen);
    const [dragOver, setDragOver] = useState(false);

    const statusInfo = status ? STATUS_MAP[status] : null;

    const handleDragOver = useCallback((e: DragEvent) => {
      if (locked) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    }, [locked]);

    const handleDragLeave = useCallback(() => {
      setDragOver(false);
    }, []);

    const handleDrop = useCallback(
      (e: DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (!file || !onFileDrop) return;

        const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
        if (
          acceptExtensions.some(
            (a) => a.toLowerCase() === ext
          )
        ) {
          if (!open) setOpen(true);
          onFileDrop(file);
        } else {
          onFileDropError?.(file);
        }
      },
      [acceptExtensions, onFileDrop, onFileDropError, open]
    );

    return (
      <div
        ref={ref}
        className={cn(
          "relative rounded-xl border-2 bg-white min-w-[600px]",
          "transition-[border-color,box-shadow] duration-200",
          open && !locked
            ? "border-primary-300 shadow-[0_0_0_2px_rgba(26,115,232,0.12)] z-10"
            : "border-gray-300",
          locked && "opacity-80",
          dragOver &&
            "!border-primary-700 !shadow-[0_0_0_3px_rgba(26,115,232,0.2)] !bg-primary-100",
          className
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        {...props}
      >
        {/* Drag overlay */}
        {dragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-100/60 pointer-events-none rounded-xl">
            <div className="flex items-center gap-2.5 rounded-lg border-2 border-dashed border-primary-700 bg-white px-8 py-4 shadow-lg">
              <UploadBlack size={20} className="text-primary-700" />
              <span className="text-[14px] font-semibold text-primary-700">
                {acceptExtensions.join(", ")} 파일을 놓으세요
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-between px-5 py-3.5",
            "cursor-pointer select-none bg-transparent",
            "transition-colors",
            open
              ? "rounded-t-[10px] hover:bg-gray-100 border-b border-gray-200"
              : "rounded-[10px] hover:bg-gray-100"
          )}
          onClick={() => setOpen((v) => !v)}
        >
          <div className="flex items-center gap-2.5">
            <RightIcon
              size={18}
              className={cn(
                "shrink-0 text-gray-600 transition-transform duration-200",
                open && "rotate-90"
              )}
            />
            <div className="flex flex-col items-start">
              <span className="text-[15px] font-semibold text-gray-900">
                {title}
              </span>
              {description && (
                <span className="text-[12px] font-normal text-gray-600">
                  {description}
                </span>
              )}
            </div>
            {statusInfo && (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5",
                  "text-[12px] font-bold",
                  statusInfo.badgeClass
                )}
              >
                <span
                  className={cn(
                    "inline-block w-1.5 h-1.5 rounded-full",
                    statusInfo.dotClass
                  )}
                />
                {statusInfo.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-medium text-gray-600 mr-0.5">
              가능한 확장자
            </span>
            {acceptExtensions.map((ext) => (
              <span
                key={ext}
                className="rounded bg-gray-100 border border-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-700 font-mono"
              >
                {ext}
              </span>
            ))}
          </div>
        </button>

        {/* Body */}
        {open && <div className="px-5 py-4">{children}</div>}
      </div>
    );
  }
);
ParsingAccordion.displayName = "ParsingAccordion";

export { ParsingAccordion };
